import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

import { TextColorSchema } from './use-load-invoice-appearance';

const URL = '/invoice_appearance';

const UpdateInvoiceAppearanceVariablesSchema = z.object({
  logo: z.union([z.instanceof(File), z.null()]).optional(),
  color: z.string().nullable().optional(),
  textColor: TextColorSchema.optional(),
});

export type UpdateInvoiceAppearanceVariables = z.infer<
  typeof UpdateInvoiceAppearanceVariablesSchema
>;

export function buildInvoiceAppearanceFormData(variables: UpdateInvoiceAppearanceVariables) {
  const parsed = UpdateInvoiceAppearanceVariablesSchema.parse(variables);
  const formData = new FormData();

  if ('logo' in parsed) formData.append('logo', parsed.logo instanceof File ? parsed.logo : '');
  if (parsed.color != null) formData.append('color', parsed.color);
  if (parsed.textColor != null) formData.append('textColor', parsed.textColor);

  return formData;
}

export async function updateInvoiceAppearanceFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateInvoiceAppearanceVariables,
) {
  const formData = buildInvoiceAppearanceFormData(variables);
  const instance = await axiosInstance;

  return instance.put(URL, formData);
}

export function useUpdateInvoiceAppearance() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateInvoiceAppearanceVariables>({
    mutationFn: variables => updateInvoiceAppearanceFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['invoiceAppearance'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateInvoiceAppearanceVariables,
    'updateInvoiceAppearance'
  >('updateInvoiceAppearance', mutation);
}
